import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, ShieldAlert, Send, Ban, Sparkles, Mail, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = [
  "informazioni_prodotti", "assistenza_ordine", "problemi_download", "pagamento_fatturazione",
  "richiesta_rimborso", "reclamo", "privacy_legale", "proposta_commerciale", "spam", "altro",
];
const CAT_LABEL = {
  informazioni_prodotti: "Informazioni prodotti", assistenza_ordine: "Assistenza ordine",
  problemi_download: "Problemi download/file", pagamento_fatturazione: "Pagamento/fatturazione",
  richiesta_rimborso: "Richiesta rimborso", reclamo: "Reclamo", privacy_legale: "Privacy/legale",
  proposta_commerciale: "Proposta commerciale", spam: "Spam", altro: "Altro",
};
const PRIORITIES = ["bassa", "normale", "alta", "urgente"];
const PRIO_COLOR = {
  bassa: "bg-slate-100 text-slate-600", normale: "bg-blue-100 text-blue-700",
  alta: "bg-amber-100 text-amber-700", urgente: "bg-red-100 text-red-700",
};
const SCOPE_LABEL = {
  in_scope: "Ambito LearnSphereHub", mixed: "Ambito misto",
  out_of_scope: "Fuori ambito", spam: "Spam",
};
const SCOPE_COLOR = {
  in_scope: "bg-emerald-100 text-emerald-700", mixed: "bg-amber-100 text-amber-700",
  out_of_scope: "bg-slate-200 text-slate-700", spam: "bg-red-100 text-red-700",
};
const STATUS_LABEL = { new: "Nuova", ignored: "Ignorata", sent: "Inviata", sent_simulated: "Inviata (simulata)" };

const EmailAssistant = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [filters, setFilters] = useState({ status: "all", category: "all", priority: "all" });

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email/status");
      setStatus(data);
    } catch (e) {
      /* status non bloccante */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "all") params.status = filters.status;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.priority !== "all") params.priority = filters.priority;
      const { data } = await api.get("/admin/email", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/email/sync", {});
      toast.success(`Sincronizzazione completata: ${data.imported} nuove (${data.source === "simulated" ? "simulate" : "IMAP"})`);
      await load();
      await loadStatus();
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (e?.response?.status === 503 || code === "imap_not_configured") {
        toast.error("IMAP non configurato: impossibile leggere la posta");
      } else {
        toast.error(formatApiError(e));
      }
      await loadStatus();
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/email/${id}`);
      setSelected(data);
      setDraft(data.ai?.draft || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const regenerate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email/${selected.id}/regenerate`);
      setSelected(data);
      setDraft(data.ai?.draft || "");
      toast.success("Bozza rigenerata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const ignore = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/email/${selected.id}/ignore`);
      toast.success("Email ignorata");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email/${selected.id}/send`, { draft, confirm: true });
      toast.success(data.simulated ? "Invio simulato (nessuna email reale spedita)" : `Risposta inviata a ${data.to}`);
      setConfirmOpen(false);
      setSelected(null);
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === "self_reply_blocked") {
        toast.error("Invio bloccato: il destinatario coincide con la casella di assistenza (anti-loop)");
      } else if (code === "invalid_recipient") {
        toast.error("Destinatario non valido: impossibile determinare un indirizzo Rispondi a sicuro");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const alreadySent = selected && (selected.status === "sent" || selected.status === "sent_simulated");

  return (
    <div data-testid="email-assistant" className="grid lg:grid-cols-2 gap-6">
      {/* LEFT: list */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading font-semibold text-brand-navy flex items-center gap-2"><Mail className="h-5 w-5 text-brand-electric" /> Assistente Email</h3>
          <Button data-testid="email-sync-btn" onClick={sync} disabled={syncing}
            className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sincronizza posta
          </Button>
        </div>

        {status && (
          <div data-testid="email-status-bar" className="mb-4 rounded-xl border border-brand-navy/10 bg-white p-3 text-sm">
            <div className="flex items-center gap-2">
              {status.imap_configured ? (
                <span data-testid="imap-status-ok" className="inline-flex items-center gap-1.5 font-semibold text-green-700"><CheckCircle2 className="h-4 w-4" /> IMAP configurato</span>
              ) : (
                <span data-testid="imap-status-ko" className="inline-flex items-center gap-1.5 font-semibold text-red-600"><XCircle className="h-4 w-4" /> IMAP non configurato</span>
              )}
              <span className="text-brand-navy/40">·</span>
              <span className="text-brand-navy/60">{status.imap_host}:{status.imap_port}</span>
            </div>
            <div className="mt-1 text-xs text-brand-navy/60">
              Ultima sincronizzazione: <b>{status.last_sync_source || "none"}</b>
              {typeof status.last_sync_imported === "number" ? ` · ${status.last_sync_imported} importate` : ""}
              {status.last_sync_at ? ` · ${new Date(status.last_sync_at).toLocaleString()}` : ""}
            </div>
            {status.last_error && (
              <div data-testid="email-status-error" className="mt-1 text-xs text-red-600">Ultimo errore: {status.last_error}</div>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-4">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="email-filter-status" className="text-sm"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="new">Nuove</SelectItem>
              <SelectItem value="ignored">Ignorate</SelectItem>
              <SelectItem value="sent">Inviate</SelectItem>
              <SelectItem value="sent_simulated">Inviate (simulate)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="email-filter-category" className="text-sm"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[c]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
            <SelectTrigger data-testid="email-filter-priority" className="text-sm"><SelectValue placeholder="Priorità" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le priorità</SelectItem>
              {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          {loading && <p className="text-sm text-brand-navy/50">Caricamento…</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-brand-navy/50" data-testid="email-empty">Nessuna email. Usa “Sincronizza posta”.</p>
          )}
          {items.map((e) => (
            <button key={e.id} data-testid={`email-row-${e.id}`} onClick={() => openDetail(e.id)}
              className={`w-full text-left rounded-xl border p-3 transition-colors ${selected?.id === e.id ? "border-brand-electric bg-brand-electric/5" : "border-brand-navy/10 bg-white hover:border-brand-electric/50"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-brand-navy text-sm truncate">{e.from_name || e.from_email}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${PRIO_COLOR[e.ai?.priority] || "bg-slate-100"}`}>{e.ai?.priority}</span>
              </div>
              <div className="text-sm text-brand-navy/80 truncate">{e.subject || "(senza oggetto)"}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {e.ai?.scope && (
                  <span data-testid={`email-scope-${e.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${SCOPE_COLOR[e.ai.scope] || "bg-slate-100 text-slate-600"}`}>{SCOPE_LABEL[e.ai.scope] || e.ai.scope}</span>
                )}
                <span className="text-[11px] text-brand-navy/50">{CAT_LABEL[e.ai?.category] || e.ai?.category}</span>
                <span className="text-[11px] text-brand-navy/40">· {STATUS_LABEL[e.status] || e.status}</span>
                {e.ai?.human_review && <span className="text-[10px] font-bold text-red-600 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Controllo umano</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: detail */}
      <div>
        {!selected ? (
          <div className="rounded-2xl border border-dashed border-brand-navy/15 p-8 text-center text-brand-navy/40 text-sm" data-testid="email-detail-empty">
            Seleziona un’email per vedere riepilogo AI e bozza.
          </div>
        ) : (
          <div className="rounded-2xl border border-brand-navy/10 bg-white p-5 space-y-4" data-testid="email-detail">
            <div>
              <div className="text-xs text-brand-navy/50">Da</div>
              <div className="font-medium text-brand-navy break-all">{selected.from_name} &lt;{selected.from_email}&gt;</div>
              <div className="text-xs text-brand-navy/50 mt-1">Rispondi a</div>
              <div data-testid="email-reply-to" className="text-brand-navy break-all">{selected.reply_to_address || "—"}</div>
              <div className="text-xs text-brand-navy/50 mt-1">Destinatario della risposta</div>
              <div data-testid="email-reply-recipient" className="font-medium text-brand-electric break-all">{selected.reply_recipient || "—"}</div>
              <div className="text-xs text-brand-navy/50 mt-1">Oggetto</div>
              <div className="text-brand-navy">{selected.subject || "(senza oggetto)"}</div>
              <div className="text-[11px] text-brand-navy/40 mt-1">{selected.date}</div>
            </div>

            {selected.ai?.human_review && (
              <div data-testid="email-human-review" className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm font-semibold">
                <ShieldAlert className="h-4 w-4" /> Controllo umano obbligatorio
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-brand-navy/50 text-xs block">Categoria</span>{CAT_LABEL[selected.ai?.category]}</div>
              <div data-testid="email-detail-scope" className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-brand-navy/50 text-xs block">Ambito</span>{SCOPE_LABEL[selected.ai?.scope] || selected.ai?.scope || "—"}</div>
              <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-brand-navy/50 text-xs block">Priorità</span>{selected.ai?.priority}</div>
              <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-brand-navy/50 text-xs block">Tono</span>{selected.ai?.tone || "—"}</div>
              <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-brand-navy/50 text-xs block">Affidabilità</span>{Math.round((selected.ai?.confidence || 0) * 100)}%</div>
            </div>

            <div>
              <div className="text-xs text-brand-navy/50 mb-1">Riepilogo AI</div>
              <p className="text-sm text-brand-navy/80">{selected.ai?.summary || "—"}</p>
              {selected.ai?.risk && <p className="text-xs text-amber-700 mt-1">Rischio: {selected.ai.risk}</p>}
            </div>

            <details className="rounded-lg border border-brand-navy/10">
              <summary className="cursor-pointer px-3 py-2 text-sm text-brand-navy/70">Messaggio originale</summary>
              <pre data-testid="email-original" className="px-3 py-2 text-xs text-brand-navy/70 whitespace-pre-wrap break-words max-h-52 overflow-auto">{selected.body_text}</pre>
              {selected.attachments?.length > 0 && (
                <div className="px-3 py-2 border-t border-brand-navy/10 text-xs text-brand-navy/60">
                  Allegati: {selected.attachments.map((a, i) => <span key={i}>{a.name} ({a.type}, {a.size} B){i < selected.attachments.length - 1 ? "; " : ""}</span>)}
                </div>
              )}
            </details>

            <div>
              <div className="text-xs text-brand-navy/50 mb-1">Bozza di risposta (modificabile)</div>
              {selected.ai?.scope === "out_of_scope" && (
                <p data-testid="email-scope-note-oos" className="text-xs text-slate-600 mb-1">Richiesta fuori ambito: risposta predefinita (nessun testo generato dall’AI).</p>
              )}
              {selected.ai?.scope === "spam" && (
                <p data-testid="email-scope-note-spam" className="text-xs text-red-600 mb-1">Messaggio classificato come spam: nessuna bozza generata, si consiglia di ignorare.</p>
              )}
              <Textarea data-testid="email-draft" rows={9} value={draft} onChange={(e) => setDraft(e.target.value)}
                disabled={alreadySent} className="text-sm" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button data-testid="email-regenerate" variant="outline" onClick={regenerate} disabled={busy || alreadySent}
                className="rounded-full text-brand-navy gap-2"><Sparkles className="h-4 w-4" /> Rigenera bozza</Button>
              <Button data-testid="email-ignore" variant="outline" onClick={ignore} disabled={busy}
                className="rounded-full text-brand-navy gap-2"><Ban className="h-4 w-4" /> Ignora</Button>
              <Button data-testid="email-send" onClick={() => setConfirmOpen(true)} disabled={busy || alreadySent || !draft.trim()}
                className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-2"><Send className="h-4 w-4" /> Approva e invia</Button>
            </div>
            {alreadySent && <p className="text-xs text-green-700">{STATUS_LABEL[selected.status]}</p>}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-white" data-testid="email-confirm-dialog">
          <DialogHeader><DialogTitle>Conferma invio risposta</DialogTitle></DialogHeader>
          <div className="text-sm text-brand-navy/80 space-y-1">
            <p><span className="text-brand-navy/50">Destinatario:</span> <b data-testid="confirm-recipient">{selected?.reply_recipient || "—"}</b></p>
            <p><span className="text-brand-navy/50">Oggetto:</span> {selected && (selected.subject?.toLowerCase().startsWith("re:") ? selected.subject : `Re: ${selected.subject || ""}`)}</p>
            <p className="text-xs text-brand-navy/40 pt-2">La risposta sarà inviata esclusivamente all’indirizzo Rispondi a verificato dal backend.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full text-brand-navy" data-testid="email-confirm-cancel">Annulla</Button>
            <Button onClick={doSend} disabled={busy} className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white" data-testid="email-confirm-send">{busy ? "Invio…" : "Conferma e invia"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailAssistant;
